/**
 * The search trail: what a live pass has searched for and opened, as a short list
 * that grows, where it used to be one line rewritten on every step.
 *
 * A source check spends most of its minute searching — up to six queries and
 * three page opens (`VERIFY_BUDGET`), after the prior-art lookup — and the live
 * pass showed only the latest of them, so a check that had already read four
 * pages looked exactly like one that had just begun. The steps are the store's
 * `searchTrail`, every line `searching` has held since the pass began, in the
 * words the engine already writes ("searching: …", "reading: host"). The whole
 * list, with every URL, still lands under "How this was checked" once the check
 * has settled (`ui/Grounding.tsx`); this is the view while it runs.
 *
 * Only the live pass's own message in the feed shows the list. The line over the
 * field is the one status line around the input, and keeps its one search line.
 */

/** Steps the store keeps for one pass. A check makes at most ten and a link
 *  scan's gathers a few more; this only bounds a stream that misbehaves. */
export const TRAIL_KEEP = 40

/** Steps the live pass lists; the ones before them are counted in one line. Four
 *  is a check's usual stride — a search, the page it opened, the next search and
 *  its page — and keeps the message's growing part shorter than its thinking. */
export const TRAIL_SHOWN = 4

/** One listed step, and how many times in a row it was taken: two pages opened
 *  on one host read "reading: arxiv.org ×2", not the same line twice. */
export interface TrailRow {
  line: string
  times: number
}

/** The rows to list, oldest first, and how many steps came before them. */
export function trailRows(
  trail: readonly string[],
  shown: number = TRAIL_SHOWN,
): { earlier: number; rows: TrailRow[] } {
  const rows: TrailRow[] = []
  for (const line of trail) {
    const last = rows.at(-1)
    if (last && last.line === line) last.times++
    else rows.push({ line, times: 1 })
  }
  const cut = Math.max(0, rows.length - shown)
  const earlier = rows.slice(0, cut).reduce((n, r) => n + r.times, 0)
  return { earlier, rows: rows.slice(cut) }
}

/** The count line over the listed steps. */
export const earlierWords = (n: number): string => `${n} earlier step${n === 1 ? '' : 's'}`
